<template>
  <div class="game-container" @click="goDetail(game)">
    <div v-if="showGameTitleBar" class="game-title">
      <div v-if="roundName" class="round-name overflow-text">{{ roundName }}</div>
      <div
        v-if="showLiveBadge"
        key="live"
        class="live-stage"
        :class="liveBadgeClass"
      >{{ liveBadgeText }}</div>
      <div
        v-else-if="plainStatusInLiveStage"
        key="game-status-plain"
        class="live-stage live-stage--plain"
      >{{ plainGameStatusText }}</div>
    </div>
    <div
      v-else-if="timeInTitleBar"
      class="game-title game-title--time-inline"
    >
      <div v-if="timeText" class="title-time">{{ timeText }}</div>
    </div>
    <div class="game-info">
      <div v-if="showLeftColumn" class="game-left">
        <div v-if="!timeInTitleBar && timeText" class="time">{{ timeText }}</div>
        <div v-if="dateText" class="date">{{ dateText }}</div>
        <div v-if="courtText" class="site">{{ courtText }}</div>
      </div>
      <div v-if="isWheelBattle" class="game-center wheel-battle-center">
        <div class="wheel-battle-box">
          <img class="wheel-battle-icon" :src="wheelBattleIconUrl" alt="" />
          <div class="wheel-battle-info">
            <div class="wheel-battle-name overflow-text">{{ wheelBattleName }}</div>
            <div class="wheel-battle-tag">限时</div>
          </div>
        </div>
      </div>
      <div v-else-if="hasMatchupTeams && game.type === 'game'" class="game-center">
        <div class="team">
          <img
            v-if="homeBadgeUrl"
            :src="homeBadgeUrl"
            alt=""
          >
          <div class="team-name overflow-text">{{ displayHomeName }}</div>
        </div>
        <div class="team">
          <img
            v-if="awayBadgeUrl"
            :src="awayBadgeUrl"
            alt=""
          >
          <div class="team-name overflow-text">{{ displayAwayName }}</div>
        </div>
      </div>
      <div v-else class="game-center game-center-empty">
        <span v-if="game.name" class="no-matchup-text">
          <img class="activity-icon" src="https://images.snsports.cn/Fn_60JJYiJSVjgy4_mDIzA5L26JY" v-if="game.type === 'activity'">
          {{ game.name }}
        </span>
        <span class="no-matchup-text" v-else>暂无对阵信息</span>
      </div>
      <div v-if="showRightColumn" class="game-right" :class="{ 'game-right--plain-status': showPlainGameStatusOnRight }">
        <template v-if="showScoreColumn">
          <div class="score">
            <span
              v-if="hasHomeScoreDisplay"
              class="card"
              :class="homeScoreHighlightClass"
            >{{ formattedHomeScore }}</span>
          </div>
          <div class="score">
            <span
              v-if="hasAwayScoreDisplay"
              class="card"
              :class="awayScoreHighlightClass"
            >{{ formattedAwayScore }}</span>
          </div>
        </template>
        <div
          v-if="showPlainGameStatusOnRight"
          class="game-status-plain"
        >{{ plainGameStatusText }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import Moment from 'moment'
import { QNImageUtil } from '@/utils'
import { goGameDetail } from '@/utils/goGameDetail'

/** 赛程卡直播角标：仅按 liveStatus.status（或裸数字）枚举，不用 statusLabel */
const liveBadgeByStatusMap = {
  [-3]: { text: '直播预告', className: 'status-upcoming' },
  0: { text: '未开播', className: 'status-upcoming' },
  1: { text: '直播中', className: 'status-live' },
  2: { text: '录像', className: 'status-finished' },
}

export default {
  props: {
    game: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      wheelBattleIconUrl: 'https://images.snsports.cn/Fmapyv1dN1-f7Mj4S5iVL2UwnMbb'
    }
  },
  computed: {
    isWheelBattle() {
      return this.game && this.game.type === 'game' && Number(this.game.gameMode) === 1
    },
    wheelBattleName() {
      const name = String(this.game.name || '').trim()
      return name || this.roundName
    },
    roundName() {
      const name = this.game.groupName + ' ' + this.game.roundName
      return name && String(name).trim() ? String(name).trim() : ''
    },
    courtText() {
      // const g = this.game
      // const court = g.fieldName || g.courtName || g.location || g.gameCourtName
      // return court && String(court).trim() ? String(court).trim() : ''
      return ''
    },
    timeText() {
      if (!this.game.beginDate) return ''
      const m = Moment(this.game.beginDate)
      return m.isValid() ? m.format('HH:mm') : ''
    },
    dateText() {
      if (!this.game.beginDate) return ''
      const m = Moment(this.game.beginDate)
      return m.isValid() ? m.format('YY/M/D') : ''
    },
    /** liveStatus：接口可能是 number/string 或对象；对象时取 liveStatus.status */
    liveStatusNormalizedCode() {
      const ls = this.game.liveStatus
      if (ls == null || ls === '') return null
      if (typeof ls === 'number' && !Number.isNaN(ls)) return ls
      if (typeof ls === 'string' && ls.trim() !== '') {
        const n = Number(ls)
        return Number.isNaN(n) ? null : n
      }
      if (typeof ls === 'object' && !Array.isArray(ls)) {
        const st = ls.status
        if (st === '' || st == null || st === undefined) return null
        const n = Number(st)
        return Number.isNaN(n) ? null : n
      }
      return null
    },
    /**
     * 展示直播角标：仅以 status 为准（不使用 statusLabel）
     * -1 未开通；其余见 liveBadgeByStatusMap
     */
    showLiveBadge() {
      const code = this.liveStatusNormalizedCode
      if (code == null || code === -1) return false
      return Object.prototype.hasOwnProperty.call(liveBadgeByStatusMap, code)
    },
    liveBadgeText() {
      const code = this.liveStatusNormalizedCode
      if (code == null || code === -1) return ''
      const row = liveBadgeByStatusMap[code]
      return row ? row.text : ''
    },
    liveBadgeClass() {
      const code = this.liveStatusNormalizedCode
      if (code == null || code === -1) return 'status-delayed'
      const row = liveBadgeByStatusMap[code]
      return row ? row.className : 'status-delayed'
    },
    hasMatchupTeams() {
      const g = this.game
      const ids = !!(g.homeTeamId && g.awayTeamId)
      const home = String(g.homeName || '').trim()
      const away = String(g.awayName || '').trim()
      return ids || !!(home && away)
    },
    displayHomeName() {
      return String(this.game.homeName || '').trim() || ''
    },
    displayAwayName() {
      return String(this.game.awayName || '').trim() || ''
    },
    homeBadgeUrl() {
      const url = this.game.homeBadge
      return url ? QNImageUtil.imageView(url, 48, 48) : ''
    },
    awayBadgeUrl() {
      const url = this.game.awayBadge
      return url ? QNImageUtil.imageView(url, 48, 48) : ''
    },
    homeScoreNum() {
      const v = this.game.homeScore
      if (v === '' || v == null || v === undefined) return null
      const n = Number(v)
      return Number.isNaN(n) ? null : n
    },
    awayScoreNum() {
      const v = this.game.awayScore
      if (v === '' || v == null || v === undefined) return null
      const n = Number(v)
      return Number.isNaN(n) ? null : n
    },
    hasHomeScoreDisplay() {
      return this.homeScoreNum !== null
    },
    hasAwayScoreDisplay() {
      return this.awayScoreNum !== null
    },
    formattedHomeScore() {
      return this.homeScoreNum !== null ? String(this.homeScoreNum) : ''
    },
    formattedAwayScore() {
      return this.awayScoreNum !== null ? String(this.awayScoreNum) : ''
    },
    /** 两方都有有效比分时，较高分一侧使用 victory */
    homeScoreHighlightClass() {
      return this.scoreSideClass('home')
    },
    awayScoreHighlightClass() {
      return this.scoreSideClass('away')
    },
    showScoreColumn() {
      return this.hasHomeScoreDisplay || this.hasAwayScoreDisplay
    },
    /** 无直播时在右侧展示的比赛状态（接口 gameStatus: 0未开始 1进行中 2已结束） */
    plainGameStatusText() {
      if (this.showLiveBadge) return ''
      const raw = this.game.gameStatus
      if (raw === '' || raw === undefined || raw === null) return ''
      const n = Number(raw)
      if (Number.isNaN(n)) return ''
      if (n === 0) return '未开始'
      if (n === 1) return '进行中'
      if (n === 2) return '已结束'
      return ''
    },
    showPlainGameStatus() {
      return !!(this.plainGameStatusText && (this.hasMatchupTeams || this.isWheelBattle))
    },
    /** 有比分且非直播时，状态文案放在标题栏 live-stage 位置 */
    plainStatusInLiveStage() {
      return this.showScoreColumn && this.showPlainGameStatus
    },
    /** 无比分时状态仍在右侧；有比分时已在标题栏，右侧不再重复 */
    showPlainGameStatusOnRight() {
      return this.showPlainGameStatus && !this.plainStatusInLiveStage
    },
    showGameTitleBar() {
      return !!(this.roundName || this.showLiveBadge || this.plainStatusInLiveStage)
    },
    /** 无标题栏状态时，仅将开赛「时分」置于原 game-title 行；日期仍在左侧栏 */
    timeInTitleBar() {
      return !this.showGameTitleBar && !!String(this.timeText || '').trim()
    },
    showRightColumn() {
      if (this.isWheelBattle) {
        return this.showPlainGameStatusOnRight
      }
      if (!this.hasMatchupTeams) return false
      return this.showScoreColumn || this.showPlainGameStatusOnRight
    },
    showLeftColumn() {
      if (this.timeInTitleBar) {
        return !!(this.dateText || this.courtText)
      }
      return !!(this.timeText || this.dateText || this.courtText)
    }
  },
  methods: {
    scoreSideClass(side) {
      const h = this.homeScoreNum
      const a = this.awayScoreNum
      if (h === null || a === null) return 'neutral'
      if (h === a) return 'neutral'
      if (side === 'home') {
        return h > a ? 'victory' : 'defeat'
      }
      return a > h ? 'victory' : 'defeat'
    },
    goDetail(game) {
      goGameDetail(this.$router, game)
    }
  }
};
</script>

<style scoped lang="scss">
.game-container{
  min-height: 110px;
  border-radius: 5px;
  background: #F9F9F9;
  margin-top: 12px;
  .game-title{
    min-height: 30px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 6px;
    .round-name{
      font-size: 14px;
      color: #303133;
      flex: 1;
      min-width: 0;
    }
    .live-stage:only-child {
      margin-left: auto;
    }
    .live-stage{
      min-width: 47px;
      width: auto;
      max-width: 50%;
      height: 18px;
      border-radius: 2px;
      color: #fff;
      font-size: 11px;
      text-align: center;
      line-height: 18px;
      padding: 0 4px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex-shrink: 0;
      margin-left: 6px;
    }
    .status-upcoming{
      background: #E6A23C;
    }
    .status-live{
      background: #C30D23;
    }
    .status-finished{
      background: #529B2E;
    }
    .status-delayed{
      background: #909399;
    }
    .live-stage--plain{
      background: transparent;
      color: #73767A;
      font-size: 12px;
      line-height: 16px;
      height: auto;
      min-height: 16px;
      padding: 0 2px;
    }
    &--time-inline{
      justify-content: flex-start;
      align-items: center;
      min-height: 30px;
      .title-time{
        color: #337ECC;
        font-size: 12px;
        font-weight: 600;
        line-height: 22px;
      }
    }
  }
  .game-info{
    display: flex;
    align-items: center;
    margin-top: 4px;
    .game-left{
      width: 60px;
      flex-shrink: 0;
      color: #337ECC;
      font-size: 12px;
      margin-top: 12px;
      padding-left: 6px;
      line-height: 16px;
      .time{
        font-weight: 600;
        line-height: 22px;
      }
    }
    .game-center{
      flex: 1;
      display: flex;
      flex-direction: column;
      min-width: 0;

      .team + .team {
        margin-top: 12px;
      }
      .team{
        display: flex;
        align-items: center;
        img{
          width: 24px;
          height: 24px;
          margin-right: 10px;
          flex-shrink: 0;
          object-fit: contain;
          border-radius: 2px;
        }
        .team-name{
          flex: 1;
          font-size: 14px;
          font-weight: 600;
          color: #73767A;
        }
      }
    }
    .game-center-empty{
      justify-content: center;
      align-items: self-start;
      align-self: stretch;
      min-height: 64px;
      .no-matchup-text{
        font-size: 14px;
        color: #909399;
        text-align: center;
        display: flex;
        align-items: center;
        .activity-icon{
          width: 30px;
          height: 30px;
          margin-right: 10px;
        }
      }
    }
    .wheel-battle-center {
      justify-content: center;
      align-self: stretch;
      min-height: 64px;
    }
    .wheel-battle-box {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      min-width: 0;
      padding: 0 8px;
      box-sizing: border-box;
    }
    .wheel-battle-icon {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      flex-shrink: 0;
      display: block;
    }
    .wheel-battle-info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    .wheel-battle-name {
      font-size: 15px;
      font-weight: 600;
      color: #333;
      line-height: 21px;
    }
    .wheel-battle-tag {
      display: inline-block;
      align-self: flex-start;
      margin-top: 4px;
      padding: 1px 6px;
      font-size: 11px;
      line-height: 16px;
      color: #e6a23c;
      background: #fdf0e8;
      border-radius: 2px;
    }
    .game-right{
      width: 60px;
      min-width: 52px;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding-right: 2px;

      > * + * {
        margin-top: 12px;
      }
      &.game-right--plain-status {
        width: auto;
        max-width: 72px;
      }
      .game-status-plain{
        font-size: 12px;
        color: #73767A;
        line-height: 16px;
        text-align: center;
        white-space: nowrap;
      }
      .score{
        height: 24px;
        text-align: center;
        line-height: 24px;
        font-size: 14px;
        font-weight: bold;
        .card{
          display: inline-block;
          min-width: 20px;
          border-radius: 3px;
          padding: 0 2px;
        }
        .victory{
          background-color: #ECF5FF;
          color: #337ECC;
        }
        .defeat{
          background-color: #E9E9EB;
          color: #909399;
        }
        .neutral{
          background-color: transparent;
          color: #303133;
        }
      }
    }
  }
}
</style>
